import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Input, Button, Card, Alert, Row, Col, message, Popconfirm } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navbar, ProductVariantDisplay, Spinner } from 'components';
import { withEventBus, withScreenLoader } from 'context';
import * as barcodeScanSelectors from 'redux/selectors/productVariant';
import * as barcodeScanActions from 'redux/actions/productVariant';
import { PRODUCT_VARIANT_UPDATE_STATUS_SUCCESS } from 'redux/actions/productVariant/action_types';
import { buildPutInStockPayload } from 'Utils/productVariant';
import styles from './barcodeScanner.module.less';

const SCAN_TIMEOUT = 100;
const MIN_BARCODE_LENGTH = 4;

class BarcodeScanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      upcCode: '',
      lastSearchTerm: '',
      isScanning: false,
      scanBuffer: '',
      lastKeyTime: 0
    };
    this.inputRef = React.createRef();
    this.subscriptions = [];
  }

  componentDidMount() {
    this.inputRef.current && this.inputRef.current.focus();
    document.addEventListener('keydown', this.handleKeyDown);
    this.subscriptions.push(
      this.props.eventBusSubscribe(PRODUCT_VARIANT_UPDATE_STATUS_SUCCESS, () => {
        this.props.hideScreenLoader();
        const { lastSearchTerm } = this.state;
        if (lastSearchTerm) {
          this.props.findByUpc(lastSearchTerm);
        }
      })
    );
  }

  componentDidUpdate(prevProps) {
    if (this.props.barcodeScanResult && !prevProps.barcodeScanResult) {
      this.setState({ upcCode: '' });
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.props.unloadBarcodeScan();
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = event => {
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return;
    }

    const currentTime = Date.now();
    const { lastKeyTime, scanBuffer } = this.state;

    if (event.key === 'Enter') {
      event.preventDefault();
      if (scanBuffer.length >= MIN_BARCODE_LENGTH) {
        this.handleScanComplete(scanBuffer);
      }
      this.setState({ scanBuffer: '', lastKeyTime: 0 });
      return;
    }

    if (event.key.length === 1) {
      if (currentTime - lastKeyTime > SCAN_TIMEOUT && scanBuffer.length > 0) {
        this.setState({ scanBuffer: event.key, lastKeyTime: currentTime });
      } else {
        this.setState({
          scanBuffer: scanBuffer + event.key,
          lastKeyTime: currentTime
        });
      }
    }
  };

  handleScanComplete = upcCode => {
    this.setState({ upcCode, lastSearchTerm: upcCode, isScanning: true });
    this.props.findByUpc(upcCode);
  };

  handleInputChange = e => {
    this.setState({ upcCode: e.target.value });
  };

  handleManualSubmit = () => {
    const { upcCode } = this.state;
    if (upcCode && upcCode.length >= MIN_BARCODE_LENGTH) {
      this.handleScanComplete(upcCode);
    } else {
      message.warning('Please enter a valid UPC code or Variant Number');
    }
  };

  handleKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleManualSubmit();
    }
  };

  handleReset = () => {
    this.setState({ upcCode: '', lastSearchTerm: '', isScanning: false, scanBuffer: '', lastKeyTime: 0 });
    this.props.unloadBarcodeScan();
    this.inputRef.current && this.inputRef.current.focus();
  };

  handleViewDetails = () => {
    const { barcodeScanResult } = this.props;
    if (barcodeScanResult) {
      window.open(
        `/catalog/productVariants/product/${barcodeScanResult.productId}/variant/${barcodeScanResult.id}/`,
        '_blank'
      );
    }
  };

  handleToggleEnabled = () => {
    const { barcodeScanResult } = this.props;
    this.props.showScreenLoader();
    this.props.updateStatus({
      productId: barcodeScanResult.productId,
      variantId: barcodeScanResult.id,
      stockStatus: barcodeScanResult.stockStatus,
      enabled: !barcodeScanResult.enabled
    });
  };

  handleToggleStockStatus = () => {
    const { barcodeScanResult } = this.props;
    this.props.showScreenLoader();
    if (barcodeScanResult.stockStatus === 'OUT_OF_STOCK') {
      this.props.updateStatus(buildPutInStockPayload(barcodeScanResult.productId, barcodeScanResult.id));
    } else {
      this.props.updateStatus({
        productId: barcodeScanResult.productId,
        variantId: barcodeScanResult.id,
        stockStatus: 'OUT_OF_STOCK',
        enabled: barcodeScanResult.enabled
      });
    }
  };

  render() {
    const { upcCode } = this.state;
    const { barcodeScanResult, isBarcodeScanLoaded, barcodeScanError } = this.props;

    let content = null;
    if (this.state.isScanning && !isBarcodeScanLoaded) {
      content = (
        <div className={styles.loadingContainer}>
          <Spinner />
          <p>Looking up: {upcCode}...</p>
        </div>
      );
    } else if (isBarcodeScanLoaded && barcodeScanResult) {
      content = (
        <div className={styles.resultContainer}>
          <Alert
            message="Product Variant Found"
            description={`Successfully found: ${barcodeScanResult.name}`}
            type="success"
            showIcon
            className={styles.alert}
          />
          <Card className={styles.resultCard}>
            <ProductVariantDisplay productVariant={barcodeScanResult} compact />
          </Card>
          <div className={styles.statusButtons}>
            <Popconfirm
              title={
                barcodeScanResult.enabled
                  ? 'Are you sure you want to disable this variant?'
                  : 'Are you sure you want to enable this variant?'
              }
              onConfirm={this.handleToggleEnabled}
              okText="Yes"
              cancelText="No"
            >
              <Button type={barcodeScanResult.enabled ? 'danger' : 'primary'} size="large">
                <FontAwesomeIcon
                  icon={barcodeScanResult.enabled ? 'ban' : 'check-square'}
                  style={{ marginRight: 8 }}
                />
                {barcodeScanResult.enabled ? 'Disable' : 'Enable'}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={
                barcodeScanResult.stockStatus === 'OUT_OF_STOCK'
                  ? 'Are you sure you want to put this variant back in stock?'
                  : 'Are you sure you want to mark this variant as out of stock?'
              }
              onConfirm={this.handleToggleStockStatus}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type={barcodeScanResult.stockStatus === 'OUT_OF_STOCK' ? 'primary' : 'danger'}
                size="large"
              >
                <FontAwesomeIcon
                  icon={barcodeScanResult.stockStatus === 'OUT_OF_STOCK' ? 'check' : 'times'}
                  style={{ marginRight: 8 }}
                />
                {barcodeScanResult.stockStatus === 'OUT_OF_STOCK' ? 'Put In Stock' : 'Out of Stock'}
              </Button>
            </Popconfirm>
          </div>
          <div className={styles.actionButtons}>
            <Button type="primary" size="large" onClick={this.handleViewDetails}>
              <FontAwesomeIcon icon="eye" style={{ marginRight: 8 }} />
              View Full Details
              <FontAwesomeIcon icon="external-link-alt" style={{ marginLeft: 8 }} />
            </Button>
            <Button size="large" onClick={this.handleReset}>
              <FontAwesomeIcon icon="barcode" style={{ marginRight: 8 }} />
              Scan Another
            </Button>
          </div>
        </div>
      );
    } else if (isBarcodeScanLoaded && barcodeScanError) {
      content = (
        <div className={styles.errorContainer}>
          <Alert
            message="Product Variant Not Found"
            description={`No product variant found for: ${upcCode}`}
            type="error"
            showIcon
            className={styles.alert}
          />
          <Button type="primary" size="large" onClick={this.handleReset}>
            <FontAwesomeIcon icon="barcode" style={{ marginRight: 8 }} />
            Try Again
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.barcodeScanner}>
        <Navbar title="Search Product Variant">
          <Button shape="circle" onClick={() => this.props.history.push('/catalog/productVariants/')}>
            <FontAwesomeIcon icon="th-list" />
          </Button>
        </Navbar>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Scan UPC / Variant Number" className={styles.scannerCard}>
              <p className={styles.instructions}>
                Use a barcode scanner to scan the UPC code, or manually enter the code below. You can also enter a
                Variant Number to look up a product variant. The scanner will auto-submit when a valid barcode is
                detected.
              </p>
              <Input
                ref={this.inputRef}
                size="large"
                placeholder="Enter or scan UPC code or Variant Number..."
                value={upcCode}
                onChange={this.handleInputChange}
                onKeyPress={this.handleKeyPress}
                className={styles.upcInput}
                autoFocus
              />
              <Button
                type="primary"
                size="large"
                block
                onClick={this.handleManualSubmit}
                className={styles.submitButton}
              >
                <FontAwesomeIcon icon="search" style={{ marginRight: 8 }} />
                Search
              </Button>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            {content}
          </Col>
        </Row>
      </div>
    );
  }
}

const mapDispatchToProps = {
  findByUpc: barcodeScanActions.findByUpc,
  unloadBarcodeScan: barcodeScanActions.unloadBarcodeScan,
  updateStatus: barcodeScanActions.updateStatus
};

const mapStateToProps = state => ({
  barcodeScanResult: barcodeScanSelectors.getBarcodeScanResult(state),
  isBarcodeScanLoaded: barcodeScanSelectors.isBarcodeScanLoaded(state),
  barcodeScanError: barcodeScanSelectors.getBarcodeScanError(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(withScreenLoader(withEventBus(BarcodeScanner)));
